/**
 * G1 / L1–L3 / H1–H4 / R2–R4 / G19 — OIDC cookie land.
 *
 * Sequence: IdP → BE cookies + 302 /{authTenant}/…?oidcLand=1 → FE single-shot
 * credentials `/me` (via {@link fetchPlatformMe} ceiling + guest latch) →
 * `/me` **200** → best-effort claim → strip → hydrate.
 *
 * G19 — land authority is credentials `/me` **200** only.
 * BAN treating memory JWT / JS-readable access cookie as land proof.
 * BAN 50ms settle poll of `/me` (deleted). Callers must use single-shot
 * {@link completeOidcCookieLand} (+ optional H1 spaced retries). `missing_cred`
 * is terminal fail (mismatch + strip); {@link fetchPlatformMe} already guest-latches.
 *
 * H1 in-tab retry (wired by CSP via {@link runOidcCookieLandMount}):
 * {@link OIDC_LAND_INTAB_RETRY_ATTEMPTS} × {@link OIDC_LAND_INTAB_RETRY_INTERVAL_MS};
 * each attempt is one `/me`, not a poll.
 *
 * Post–H1 give-up (CSP-owned via {@link runOidcCookieLandMount}):
 * - **R3** — `oidcSoftAuthBudgetExhaustedRef` stays true while `?oidcLand=1`
 *   remains; bootstrap must not soft-reset it. Same for
 *   `oidcPostGiveUpTransientCountRef` (do not zero on soft-guest remount).
 * - **H2 LOCK** — refs alone are wiped by `key={cspSessionKey}` remounts;
 *   persist exhausted + post-give-up count in a module store while land query
 *   remains (see {@link getOidcLandPostGiveUpBudgetState}).
 * - **R4** — one immediate in-tab definitive land (`budget:0`);
 *   visibility/pageshow stay bonus triggers (H3 debounce).
 * - **R2** — with {@link OIDC_LAND_POST_GIVEUP_TRANSIENT_ATTEMPTS} `= 1`, R4
 *   alone force-strips on perpetual transient (`handled:false` + query kept).
 */

import { claimPendingSpContactsSilent, transferGuestFavoritesOnRegistrationSilent } from '../../features/account/salesPointContactsApi.js';
import { transferGuestOfferFavoritesSilent } from '../../features/account/offerFavoritesApi.js';
import { publishCustomerAuth } from '../crossTab/customerCrossTab.js';
import { loadRuntimeConfig } from '../../config/runtimeConfig.js';
import { fetchPlatformMe } from './fetchPlatformMe.js';

export const OIDC_LAND_QUERY_PARAM = 'oidcLand';

/**
 * R1 — dispatched after {@link stripOidcLandQueryFromLocation} mutates the URL via
 * `history.replaceState` (RR does not see that). Listeners (e.g. RequireCustomerAuth)
 * must re-read `window.location.search` as SSOT for `?oidcLand=`.
 */
export const OIDC_LAND_QUERY_CHANGED_EVENT = 'customer:oidc-land-query-changed';

/**
 * @deprecated G19 — settle poll deleted. Kept for CSP call-site compat; ignored
 * (land is single-shot `/me` via {@link fetchPlatformMe}).
 */
export const OIDC_LAND_COOKIE_WAIT_MS = 0;

/**
 * H3 — min gap between visibility/pageshow land attempts (avoids unbound /me when
 * visibilitychange + pageshow fire together or while H1 is retrying).
 */
export const OIDC_LAND_VISIBILITY_MIN_INTERVAL_MS = 1000;
/** H3 alias — same value as {@link OIDC_LAND_VISIBILITY_MIN_INTERVAL_MS}. */
export const OIDC_LAND_VISIBILITY_DEBOUNCE_MS = OIDC_LAND_VISIBILITY_MIN_INTERVAL_MS;

/**
 * Shared land in-flight (H1 + visibility).
 * G3 — coalesce only when softAuthRejectBudgetRemaining matches; never join a
 * soft (budget &gt; 0) attempt from a definitive (0) / stricter caller.
 */
let oidcLandAttemptInFlight: Promise<CompleteOidcCookieLandResult> | null = null;
/** Budget of the in-flight land (null when idle). */
let oidcLandAttemptInFlightBudget: number | null = null;
/** Timestamp of last visibility/pageshow land slot acquisition. */
let lastVisibilityOidcLandAtMs = 0;

/**
 * H2 LOCK — module-level post–H1 give-up budget (survives CSP remount).
 * While `?oidcLand=1` remains, CSP must sync refs ↔ this store so
 * `key={cspSessionKey}` remounts do not restart soft-auth budget from 0.
 * Cleared on successful land / definitive strip / fresh OIDC soft start.
 */
export interface OidcLandPostGiveUpBudgetState {
  readonly softAuthBudgetExhausted: boolean;
  readonly postGiveUpTransientCount: number;
}

let softAuthBudgetExhausted = false;
let postGiveUpTransientCount = 0;

export function getOidcLandPostGiveUpBudgetState(): OidcLandPostGiveUpBudgetState {
  return {
    softAuthBudgetExhausted,
    postGiveUpTransientCount,
  };
}

export function setOidcLandPostGiveUpBudgetState(
  next: Partial<OidcLandPostGiveUpBudgetState>
): void {
  if (next.softAuthBudgetExhausted !== undefined) {
    softAuthBudgetExhausted = next.softAuthBudgetExhausted;
  }
  if (next.postGiveUpTransientCount !== undefined) {
    postGiveUpTransientCount = next.postGiveUpTransientCount;
  }
}

/** Clear H2 post-give-up budget (land ended or fresh soft start). */
export function clearOidcLandPostGiveUpBudgetState(): void {
  softAuthBudgetExhausted = false;
  postGiveUpTransientCount = 0;
}

export function isOidcLandAttemptInFlight(): boolean {
  return oidcLandAttemptInFlight !== null;
}

/**
 * H3 gate for visibility/pageshow: false while a land attempt is in flight or
 * within {@link OIDC_LAND_VISIBILITY_MIN_INTERVAL_MS} of the last visibility slot.
 * On true, stamps the visibility clock (caller should then run land).
 */
export function tryAcquireVisibilityOidcLandSlot(
  nowMs: number = Date.now()
): boolean {
  if (oidcLandAttemptInFlight !== null) {
    return false;
  }
  if (nowMs - lastVisibilityOidcLandAtMs < OIDC_LAND_VISIBILITY_MIN_INTERVAL_MS) {
    return false;
  }
  lastVisibilityOidcLandAtMs = nowMs;
  return true;
}

/** Test seam — clear H3 + H2 post-give-up budget coordination between suites. */
export function resetOidcLandAttemptCoordinationForTests(): void {
  oidcLandAttemptInFlight = null;
  oidcLandAttemptInFlightBudget = null;
  lastVisibilityOidcLandAtMs = 0;
  clearOidcLandPostGiveUpBudgetState();
  clearOidcLandAggregateBudgetState();
}

/**
 * H1 — delay between in-tab land retries while `?oidcLand=1` remains unhandled.
 * (~2–3s total with {@link OIDC_LAND_INTAB_RETRY_ATTEMPTS}).
 */
export const OIDC_LAND_INTAB_RETRY_INTERVAL_MS = 1000;

/**
 * H1 — extra in-tab `completeOidcCookieLand` attempts after the first
 * single-shot attempt (spaced; no settle poll).
 */
export const OIDC_LAND_INTAB_RETRY_ATTEMPTS = 3;

export function hasOidcLandQuery(search: string): boolean {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  );
  const raw = params.get(OIDC_LAND_QUERY_PARAM);
  return raw === '1' || raw === 'true';
}

/**
 * Wave 4.6 G9 defense — `oidcPhoneGate=1` + non-empty `token` is a pending land
 * (no shopping session yet). Count as landing-pending so guards do not bounce
 * to `/sign-in` while the token add-phone screen is active.
 */
export function hasOidcPhoneGateTokenPending(search: string): boolean {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  );
  const gate = params.get('oidcPhoneGate');
  const token = params.get('token');
  return (
    (gate === '1' || gate === 'true') &&
    token !== null &&
    token.trim().length > 0
  );
}

/**
 * G4 — optional OIDC phone offer (`oidcOfferPhone=1`) is a pending land.
 * Park restore must not race ahead to the parked shop while add-phone
 * skip/complete is still active; clears when the offer navigates away.
 */
export function hasOidcOfferPhonePending(search: string): boolean {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  );
  const raw = params.get('oidcOfferPhone');
  return raw === '1' || raw === 'true';
}

/**
 * True when Account-link `oidcLand=1`, phone-gate token land, **or** optional
 * phone-offer land is pending.
 */
export function hasOidcLandingPending(search: string): boolean {
  return (
    hasOidcLandQuery(search) ||
    hasOidcPhoneGateTokenPending(search) ||
    hasOidcOfferPhonePending(search)
  );
}

/**
 * R2 / F2 LOCK option 1 — after H1 give-up, max post-give-up **definitive**
 * (`softAuthRejectBudgetRemaining:0`) lands that return `handled:false` while
 * `?oidcLand=1` is still present before CSP force-strips. Set to **1** so **R4
 * alone** ends perpetual transient; visibility/pageshow remain bonus (H3) and
 * do not need to fire. Mismatch already strips inside
 * {@link completeOidcCookieLand} (does not consume this budget).
 */
export const OIDC_LAND_POST_GIVEUP_TRANSIENT_ATTEMPTS = 1;

/**
 * G7 — aggregate wall-clock ceiling for OIDC land hold on auth/account routes.
 * Guards stop waiting after this budget even if `?oidcLand=1` (or phone-gate land)
 * remains; CSP / guard give-up force-strips pending query params.
 */
export const OIDC_LAND_AGGREGATE_BUDGET_MS = 15_000;

let oidcLandAggregateStartedAtMs: number | null = null;
let oidcLandAggregateGiveUpRecorded = false;

/** Stamp aggregate land start (first pending query in tab). Idempotent. */
export function noteOidcLandAggregateStart(nowMs: number = Date.now()): void {
  if (oidcLandAggregateStartedAtMs === null) {
    oidcLandAggregateStartedAtMs = nowMs;
  }
}

export function isOidcLandAggregateBudgetExhausted(
  nowMs: number = Date.now()
): boolean {
  if (oidcLandAggregateStartedAtMs === null) {
    return false;
  }
  return nowMs - oidcLandAggregateStartedAtMs >= OIDC_LAND_AGGREGATE_BUDGET_MS;
}

/** Ms until aggregate budget elapses; null when land clock not started. */
export function msUntilOidcLandAggregateBudgetExhausted(
  nowMs: number = Date.now()
): number | null {
  if (oidcLandAggregateStartedAtMs === null) {
    return null;
  }
  return Math.max(
    0,
    OIDC_LAND_AGGREGATE_BUDGET_MS - (nowMs - oidcLandAggregateStartedAtMs),
  );
}

/** Clear aggregate land clock (no pending query or successful land). */
export function clearOidcLandAggregateBudgetState(): void {
  oidcLandAggregateStartedAtMs = null;
  oidcLandAggregateGiveUpRecorded = false;
}

/**
 * G7 — force-end hung land: strip all landing-pending query params and notify
 * guards. Idempotent per tab until pending query returns.
 */
export function giveUpOidcLandAggregateBudget(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (oidcLandAggregateGiveUpRecorded) {
    return false;
  }
  oidcLandAggregateGiveUpRecorded = true;
  stripOidcLandingPendingFromLocation();
  oidcLandAggregateStartedAtMs = null;
  clearOidcLandPostGiveUpBudgetState();
  return true;
}

export interface OidcLandGuardPendingState {
  readonly oidcLandingPending: boolean;
  readonly oidcLandBudgetExhausted: boolean;
}

/**
 * G7 — guard SSOT: pending only while aggregate budget remains. Does not mutate
 * URL (call {@link giveUpOidcLandAggregateBudget} from effects / CSP timer).
 */
export function readOidcLandGuardPending(
  search: string,
  nowMs: number = Date.now()
): OidcLandGuardPendingState {
  if (!hasOidcLandingPending(search)) {
    clearOidcLandAggregateBudgetState();
    return { oidcLandingPending: false, oidcLandBudgetExhausted: false };
  }
  noteOidcLandAggregateStart(nowMs);
  const oidcLandBudgetExhausted = isOidcLandAggregateBudgetExhausted(nowMs);
  return {
    oidcLandingPending: !oidcLandBudgetExhausted,
    oidcLandBudgetExhausted,
  };
}

/** Strip all landing-pending query params without a navigation reload. */
export function stripOidcLandingPendingFromLocation(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  let changed = false;
  if (url.searchParams.has(OIDC_LAND_QUERY_PARAM)) {
    url.searchParams.delete(OIDC_LAND_QUERY_PARAM);
    changed = true;
  }
  if (url.searchParams.has('oidcOfferPhone')) {
    url.searchParams.delete('oidcOfferPhone');
    changed = true;
  }
  if (url.searchParams.has('oidcPhoneGate')) {
    url.searchParams.delete('oidcPhoneGate');
    url.searchParams.delete('token');
    changed = true;
  }
  if (!changed) {
    return;
  }
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
  clearOidcLandPostGiveUpBudgetState();
  window.dispatchEvent(new CustomEvent(OIDC_LAND_QUERY_CHANGED_EVENT));
}

/** Strip oidcLand from the current URL without a navigation reload. */
export function stripOidcLandQueryFromLocation(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  if (!url.searchParams.has(OIDC_LAND_QUERY_PARAM)) {
    return;
  }
  url.searchParams.delete(OIDC_LAND_QUERY_PARAM);
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
  // H2 — definitive strip / successful land ends post-give-up budget.
  clearOidcLandPostGiveUpBudgetState();
  // R1 — notify guards: window is SSOT; RR search may still show oidcLand.
  window.dispatchEvent(new CustomEvent(OIDC_LAND_QUERY_CHANGED_EVENT));
}

export interface CompleteOidcCookieLandInput {
  readonly tenantCode: string;
  readonly apiBaseUrl: string;
  readonly search?: string;
  /**
   * @deprecated G19 — not land authority. Optional dual residual for
   * best-effort claim Bearer only; land itself uses credentials `/me`.
   */
  readonly readToken?: () => string | null;
  /** Test seam — inject JWT↔URL resolution (defaults to credentials `/me`). */
  readonly resolveJwtUrlTenantMatch?: (
    tenantCode: string,
    accessToken: string
  ) => Promise<OidcJwtUrlMatchResult>;
  /**
   * @deprecated G19 — ignored (no settle poll). Kept for CSP call-site compat.
   */
  readonly cookieWaitMs?: number;
  /**
   * H4 — remaining soft-auth attempt budget from CSP (attempt count, not wall-clock ms).
   * When &gt; 0, URL-scoped /me 401/403 are transient (first-paint Set-Cookie race).
   * When 0 / omitted, 401/403 are definitive mismatch (strip). Never URL-only stamp.
   */
  readonly softAuthRejectBudgetRemaining?: number;
}

export interface ResolveOidcJwtUrlTenantMatchOptions {
  readonly softAuthRejectBudgetRemaining?: number;
}

export type OidcJwtUrlMatchResult =
  | {
      readonly kind: 'match';
      readonly homeTenantCode?: string;
      readonly customerId?: number;
    }
  | { readonly kind: 'mismatch' }
  | { readonly kind: 'transient' };

export interface CompleteOidcCookieLandResult {
  readonly handled: boolean;
  readonly wroteJwtTenantCode: boolean;
  readonly claimed: boolean;
}

/**
 * L3 / H4 / G19 — home tenant must match URL tenant via credentials
 * `GET …/customer-account/me` **200** (cookie jar). BAN JWT-from-document-cookie
 * / memory Bearer as land authority. Single-shot only (no settle poll).
 *
 * G19 — `missing_cred` is **terminal mismatch** (guest latched by
 * {@link fetchPlatformMe}). Soft budget only softens unauthorized.
 *
 * Third arg kept for call-site/test seam compat; ignored as authority.
 */
export async function resolveOidcJwtUrlTenantMatch(
  apiBaseUrl: string,
  tenantCode: string,
  _accessToken?: string | null,
  options?: ResolveOidcJwtUrlTenantMatchOptions
): Promise<OidcJwtUrlMatchResult> {
  void apiBaseUrl;
  void _accessToken;
  const browseTenant = tenantCode.trim();
  if (browseTenant.length === 0) {
    return { kind: 'mismatch' };
  }

  const softBudget = options?.softAuthRejectBudgetRemaining ?? 0;
  // forLoginLand: allow wire while CSP holds {@link beginLoginLand} around land.
  const meResult = await fetchPlatformMe(loadRuntimeConfig(), undefined, {
    forLoginLand: true,
  });

  if (meResult.kind === 'ok') {
    const customerId = meResult.me.customerId;
    const homeTenantCode = (meResult.me.tenantCode ?? browseTenant).trim();
    if (homeTenantCode.length > 0 && homeTenantCode !== browseTenant) {
      return { kind: 'mismatch' };
    }
    return { kind: 'match', homeTenantCode: browseTenant, customerId };
  }

  // missing_cred → terminal mismatch (no guest latch — silence retired).
  if (meResult.kind === 'missing_cred') {
    return { kind: 'mismatch' };
  }

  if (meResult.kind === 'unauthorized') {
    if (softBudget > 0) {
      return { kind: 'transient' };
    }
    return { kind: 'mismatch' };
  }

  // ceiling / network transient — caller may spaced-retry; no in-module poll.
  return { kind: 'transient' };
}

/**
 * When `oidcLand=1` + credentials `/me` **200** match: best-effort claim, strip query.
 * best-effort claim, strip query. Returns handled=true so CSP can hydrate.
 * H3 / G3 — concurrent callers with the **same** softAuthRejectBudgetRemaining
 * share one in-flight promise (no double /me). Different budgets do not join:
 * wait for the other attempt, then run separately so definitive budget 0 can
 * still strip on permanent 401.
 */
export async function completeOidcCookieLand(
  input: CompleteOidcCookieLandInput
): Promise<CompleteOidcCookieLandResult> {
  const requestedBudget = input.softAuthRejectBudgetRemaining ?? 0;

  while (oidcLandAttemptInFlight !== null) {
    if (oidcLandAttemptInFlightBudget === requestedBudget) {
      return oidcLandAttemptInFlight;
    }
    // G3 — soft in flight + definitive/stricter caller: wait, then own attempt.
    await oidcLandAttemptInFlight.catch(() => undefined);
  }

  const promise = runCompleteOidcCookieLand(input).finally(() => {
    if (oidcLandAttemptInFlight === promise) {
      oidcLandAttemptInFlight = null;
      oidcLandAttemptInFlightBudget = null;
    }
  });
  oidcLandAttemptInFlight = promise;
  oidcLandAttemptInFlightBudget = requestedBudget;
  return promise;
}

async function runCompleteOidcCookieLand(
  input: CompleteOidcCookieLandInput
): Promise<CompleteOidcCookieLandResult> {
  const search =
    input.search ??
    (typeof window !== 'undefined' ? window.location.search : '');
  if (!hasOidcLandQuery(search)) {
    return { handled: false, wroteJwtTenantCode: false, claimed: false };
  }

  const tenantCode = input.tenantCode.trim();
  if (tenantCode.length === 0) {
    stripOidcLandQueryFromLocation();
    return { handled: false, wroteJwtTenantCode: false, claimed: false };
  }

  const softAuthRejectBudgetRemaining =
    input.softAuthRejectBudgetRemaining ?? 0;
  const matchResolver =
    input.resolveJwtUrlTenantMatch ??
    ((code: string, accessToken: string) =>
      resolveOidcJwtUrlTenantMatch(input.apiBaseUrl, code, accessToken, {
        softAuthRejectBudgetRemaining,
      }));

  // G19 — no readable-cookie gate; no settle poll. Single credentials `/me`.
  void input.cookieWaitMs;
  void OIDC_LAND_COOKIE_WAIT_MS;
  const match = await matchResolver(tenantCode, '');

  if (match.kind === 'transient') {
    // Retry later — do not burn land signal.
    return { handled: false, wroteJwtTenantCode: false, claimed: false };
  }
  if (match.kind === 'mismatch') {
    // L3 trust refuse — definitive (after soft budget).
    stripOidcLandQueryFromLocation();
    return { handled: false, wroteJwtTenantCode: false, claimed: false };
  }

  const homeTenantCode = (match.homeTenantCode ?? tenantCode).trim();
  if (homeTenantCode.length === 0) {
    stripOidcLandQueryFromLocation();
    return { handled: false, wroteJwtTenantCode: false, claimed: false };
  }

  const wroteJwtTenantCode = false;

  let claimed = false;
  try {
    await claimPendingSpContactsSilent({
      apiBaseUrl: input.apiBaseUrl,
    });
    claimed = true;
  } catch {
    claimed = false;
  }
  const customerId =
    typeof match.customerId === 'number' && match.customerId > 0
      ? match.customerId
      : null;
  if (customerId !== null) {
    try {
      await transferGuestFavoritesOnRegistrationSilent({
        apiBaseUrl: input.apiBaseUrl,
        customerId,
      });
    } catch {
      /* best-effort */
    }
    try {
      await transferGuestOfferFavoritesSilent({
        apiBaseUrl: input.apiBaseUrl,
        customerId,
      });
    } catch {
      /* best-effort Taška★ bag claim */
    }
  }

  stripOidcLandQueryFromLocation();

  // G1 / H5 — publish login ONLY on credentials `/me` 200 land path (match above).
  // OIDC does not call establishSession; CSP hydrates via runHydrate — so land owns
  // the bus publish (same post-`/me` gate as CSP establishSession pendingLogin parity).
  // notifyLocalSubscribers:false inside publishCustomerAuth — landing tab already hydrates via CSP.
  publishCustomerAuth({
    type: 'login',
    tenantCode: homeTenantCode,
    scope: 'platform',
  });

  return { handled: true, wroteJwtTenantCode, claimed };
}
